import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { CheckCircle, XCircle, Eye, Coins, AlertTriangle } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ValidationsPage() {
  const { t } = useLang();
  const [data, setData] = useState({ receipts: [], reviews: [] });
  const [loading, setLoading] = useState(true);
  const [viewImage, setViewImage] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [correctionDlg, setCorrectionDlg] = useState(null); // { id, kind: 'review' | 'receipt' }
  const [correctionReason, setCorrectionReason] = useState('');

  const fetchPending = async () => {
    try {
      const res = await axios.get(`${API}/validations/pending`, { withCredentials: true });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPending(); }, []);

  const handleReceiptValidation = async (receiptId, action) => {
    setProcessing(receiptId);
    try {
      await axios.post(`${API}/validations/receipt/${receiptId}`, { action }, { withCredentials: true });
      toast.success(`Scontrino ${action === 'approve' ? 'approvato' : 'rifiutato'}`);
      fetchPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setProcessing(null); }
  };

  const handleReviewApprove = async (reviewId) => {
    setProcessing(reviewId);
    try {
      await axios.post(`${API}/validations/review/${reviewId}`, { action: 'approve' }, { withCredentials: true });
      toast.success('Recensione validata — reward accreditati');
      fetchPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setProcessing(null); }
  };

  const submitCorrectionRequest = async () => {
    if (!correctionDlg) return;
    if (correctionReason.trim().length < 10) {
      toast.error(t('val_correction_min_reason'));
      return;
    }
    setProcessing(correctionDlg.id);
    try {
      const endpoint = correctionDlg.kind === 'review'
        ? `${API}/validations/review/${correctionDlg.id}`
        : `${API}/validations/receipt/${correctionDlg.id}`;
      await axios.post(endpoint, { action: 'request_correction', reason: correctionReason }, { withCredentials: true });
      toast.success('Richiesta di correzione inviata');
      setCorrectionDlg(null);
      setCorrectionReason('');
      fetchPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setProcessing(null); }
  };

  const viewReceiptImage = async (imageUrl) => {
    try {
      const res = await axios.get(`${API}/files/${imageUrl}`, { withCredentials: true, responseType: 'blob' });
      setViewImage(URL.createObjectURL(res.data));
    } catch { toast.error('Could not load image'); }
  };

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></div>;

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="validations-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pt-20 sm:pt-24">
        <div className="mb-6 sm:mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2 font-sans">{t('nav_my_restaurant')}</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('val_title')}</h1>
          <p className="text-muted-foreground text-sm mt-1 font-sans">
            {data.reviews.length > 0 ? `${data.reviews.length} recensioni in attesa di validazione` : t('val_no_pending')}
          </p>
          {data.reviews.length > 0 && (
            <p className="text-xs text-muted-foreground font-sans mt-2 italic" data-testid="validation-hint">{t('val_hint_blind')}</p>
          )}
        </div>

        <Dialog open={!!viewImage} onOpenChange={() => { if (viewImage) URL.revokeObjectURL(viewImage); setViewImage(null); }}>
          <DialogContent className="max-w-2xl rounded-2xl mx-4"><DialogHeader><DialogTitle className="font-sans">Scontrino</DialogTitle><DialogDescription className="font-sans">Verifica lo scontrino</DialogDescription></DialogHeader>
            {viewImage && <img src={viewImage} alt="Receipt" className="w-full rounded-lg" />}
          </DialogContent>
        </Dialog>

        {/* Correction Request Dialog */}
        <Dialog open={!!correctionDlg} onOpenChange={() => { setCorrectionDlg(null); setCorrectionReason(''); }}>
          <DialogContent className="max-w-lg rounded-2xl mx-4" data-testid="correction-dialog">
            <DialogHeader>
              <DialogTitle className="font-sans flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" strokeWidth={1.5} />
                {t('val_correction_reason_title')}
              </DialogTitle>
              <DialogDescription className="font-sans">
                {t('val_correction_reason_placeholder')}
              </DialogDescription>
            </DialogHeader>
            <textarea
              value={correctionReason}
              onChange={e => setCorrectionReason(e.target.value)}
              rows={5}
              placeholder={t('val_correction_reason_placeholder')}
              className="w-full text-sm p-3 border border-border rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-primary font-sans"
              data-testid="correction-reason-input"
            />
            <div className="text-xs text-muted-foreground font-sans">{correctionReason.length}/500</div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => { setCorrectionDlg(null); setCorrectionReason(''); }} className="rounded-full" data-testid="correction-cancel">
                {t('val_correction_cancel')}
              </Button>
              <Button
                onClick={submitCorrectionRequest}
                disabled={correctionReason.trim().length < 10 || processing === correctionDlg?.id}
                className="rounded-full bg-amber-600 hover:bg-amber-700 btn-lift"
                data-testid="correction-submit"
              >
                <AlertTriangle className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('val_correction_send')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {data.reviews.length === 0 ? (
          <Card className="border border-border rounded-2xl"><CardContent className="p-8 text-center">
            <CheckCircle className="w-10 h-10 text-emerald-600 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-muted-foreground text-sm font-sans">{t('val_no_pending')}</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {data.reviews.map((rv, i) => (
              <Card key={rv.review_id} className="border border-border rounded-2xl card-hover" data-testid={`validation-review-${i}`}>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-semibold text-sm font-sans mb-1">{rv.reviewer_name}</p>
                      {rv.receipt_amount > 0 && (
                        <div className="bg-muted/50 rounded-lg px-3 py-2 mb-2 inline-block">
                          <p className="text-xs text-muted-foreground font-sans">{t('val_receipt_amount')}</p>
                          <p className="text-base font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>EUR {rv.receipt_amount}</p>
                        </div>
                      )}
                      {/* Coin dichiarati — always visible when > 0, highlighted YELLOW (Parte 4+18) */}
                      {rv.coins_used > 0 && (
                        <div
                          className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2 flex items-start gap-2"
                          data-testid={`declared-coins-${i}`}
                        >
                          <Coins className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" strokeWidth={1.5} />
                          <div className="flex-1">
                            <p className="text-xs font-sans font-semibold text-amber-900">
                              {t('val_declared_coins')}: <span className="font-mono">EUR {rv.coins_used}</span>
                            </p>
                            <p className="text-[11px] text-amber-800 font-sans mt-0.5">{t('val_coins_warning')}</p>
                          </div>
                        </div>
                      )}
                      {rv.receipt_image_url && (
                        <div className="mb-2">
                          <Button variant="outline" size="sm" onClick={() => viewReceiptImage(rv.receipt_image_url)} className="rounded-full text-xs" data-testid={`view-review-receipt-${i}`}>
                            <Eye className="w-3 h-3 mr-1" /> {t('val_view')} scontrino
                          </Button>
                        </div>
                      )}
                      {rv.correction_cycles > 0 && (
                        <Badge className="bg-muted text-muted-foreground text-[10px] mr-2" data-testid={`correction-cycles-${i}`}>
                          Ciclo correzione #{rv.correction_cycles}
                        </Badge>
                      )}
                      <p className="text-xs text-muted-foreground font-sans">{new Date(rv.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex sm:flex-col gap-2 shrink-0">
                      <Button
                        data-testid={`approve-review-${i}`}
                        size="sm"
                        onClick={() => handleReviewApprove(rv.review_id)}
                        disabled={processing === rv.review_id}
                        className="rounded-full text-xs bg-emerald-600 hover:bg-emerald-700 btn-lift flex-1 sm:flex-none"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" /> {t('val_validate_btn')}
                      </Button>
                      <Button
                        data-testid={`request-correction-${i}`}
                        variant="outline"
                        size="sm"
                        onClick={() => setCorrectionDlg({ id: rv.review_id, kind: 'review' })}
                        disabled={processing === rv.review_id}
                        className="rounded-full text-xs border-amber-400 text-amber-700 hover:bg-amber-50 flex-1 sm:flex-none"
                      >
                        <AlertTriangle className="w-3 h-3 mr-1" /> {t('val_request_correction')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
